import { useMemo, useState } from "react";
import type { Product } from "../../../lib/api";
import { ProductCard, isSoldOut } from "./ProductCard";
import { useStorefrontContext } from "./StorefrontContext";

type Sort = "featured" | "newest" | "price-asc" | "price-desc";

const SORTS: { id: Sort; label: string }[] = [
  { id: "featured", label: "Featured" },
  { id: "newest", label: "Newest" },
  { id: "price-asc", label: "Price: low to high" },
  { id: "price-desc", label: "Price: high to low" },
];

export function sortProducts(products: Product[], sort: Sort): Product[] {
  const list = [...products];
  switch (sort) {
    case "newest":
      return list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    case "price-asc":
      return list.sort((a, b) => a.priceMinor - b.priceMinor);
    case "price-desc":
      return list.sort((a, b) => b.priceMinor - a.priceMinor);
    default:
      return list.sort((a, b) => a.position - b.position);
  }
}

/**
 * Full product listing with collection filters, stock filter and sorting.
 * Layout comes from the catalog slot: its column count, and a sidebar for
 * the filters when the slot is horizontal.
 */
export function Catalog({ columns, sidebar = false }: { columns?: number; sidebar?: boolean } = {}) {
  const { store } = useStorefrontContext();
  const [collection, setCollection] = useState<string>("all");
  const [sort, setSort] = useState<Sort>("featured");
  const [inStockOnly, setInStockOnly] = useState(false);

  const collections = useMemo(
    () => Array.from(new Set(store.products.map((p) => p.collection).filter(Boolean))).sort(),
    [store.products],
  );

  const visible = useMemo(() => {
    const filtered = store.products.filter(
      (p) =>
        (collection === "all" || p.collection === collection) && (!inStockOnly || !isSoldOut(p)),
    );
    return sortProducts(filtered, sort);
  }, [store.products, collection, inStockOnly, sort]);

  const gridStyle = columns ? { ["--zs-cols" as string]: String(Math.min(4, Math.max(2, columns))) } : undefined;

  return (
    <div className={sidebar ? "zs-catalog zs-catalog--sidebar" : "zs-catalog"}>
      <div className="zs-toolbar">
        <div className="zs-chips" role="group" aria-label="Filter by collection">
          {["all", ...collections].map((name) => (
            <button
              key={name}
              type="button"
              className="zs-chip"
              aria-pressed={collection === name}
              onClick={() => setCollection(name)}
            >
              {name === "all" ? "All" : name}
            </button>
          ))}
        </div>
        <div className="zs-toolbar__end">
          <label className="zs-toggle">
            <input
              type="checkbox"
              checked={inStockOnly}
              onChange={(e) => setInStockOnly(e.target.checked)}
            />
            <span>In stock</span>
          </label>
          <label className="zs-select">
            <span className="zs-visually-hidden">Sort by</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              {SORTS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="zs-catalog__results">
        <p className="zs-count" aria-live="polite">
          {visible.length} {visible.length === 1 ? "product" : "products"}
        </p>

        {visible.length === 0 ? (
          <div className="zs-empty">
            <p>No products match these filters.</p>
            <button
              type="button"
              className="zs-btn zs-btn--ghost"
              onClick={() => {
                setCollection("all");
                setInStockOnly(false);
              }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="zs-grid" style={gridStyle}>
            {visible.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** A few featured products for homepage slots. */
export function FeaturedProducts({ limit, columns }: { limit: number; columns?: number }) {
  const { store } = useStorefrontContext();
  const products = sortProducts(store.products, "featured").slice(0, Math.max(1, limit));
  if (products.length === 0) return null;
  const cols = Math.min(4, Math.max(2, columns ?? products.length));
  return (
    <div className="zs-grid" style={{ ["--zs-cols" as string]: String(cols) }}>
      {products.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
}
